import { useCallback, useEffect, useRef } from 'react';

interface Rgb {
  r: number;
  g: number;
  b: number;
}

interface LetterGlitchProperties {
  glitchColors?: string[];
  glitchSpeed?: number;
  centerVignette?: boolean;
  outerVignette?: boolean;
  smooth?: boolean;
  characters?: string;
}

const FONT_SIZE = 16;
const CHAR_WIDTH = 10;
const CHAR_HEIGHT = 20;
const VIGOR_OUTER = 'bg-[radial-gradient(circle,rgba(0,0,0,0)_60%,rgba(0,0,0,1)_100%)]';
const VIGOR_CENTER = 'bg-[radial-gradient(circle,rgba(0,0,0,0.8)_0%,rgba(0,0,0,0)_60%)]';

const hexToRgb = (hex: string): Rgb | null => {
  let normalized = hex.replace('#', '').trim();
  if (normalized.length === 3) {
    const [r, g, b] = [normalized.charAt(0), normalized.charAt(1), normalized.charAt(2)];
    normalized = r + r + g + g + b + b;
  }
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalized);
  if (!result?.[1] || !result[2] || !result[3]) return null;
  return {
    r: Number.parseInt(result[1], 16),
    g: Number.parseInt(result[2], 16),
    b: Number.parseInt(result[3], 16),
  };
};

const interpolateColor = (start: Rgb, end: Rgb, factor: number) => {
  const r = Math.round(start.r + (end.r - start.r) * factor);
  const g = Math.round(start.g + (end.g - start.g) * factor);
  const b = Math.round(start.b + (end.b - start.b) * factor);
  return `rgb(${String(r)}, ${String(g)}, ${String(b)})`;
};

const randomItem = <T,>(items: T[]) => items[Math.floor(Math.random() * items.length)];

export function LetterGlitch({
  glitchColors = ['#2b4539', '#61dca3', '#61b3dc'],
  glitchSpeed = 50,
  centerVignette = false,
  outerVignette = true,
  smooth = true,
  characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$&*()-_+=/[]{};:<>.,0123456789',
}: LetterGlitchProperties) {
  const canvasReference = useRef<HTMLCanvasElement | null>(null);
  const animationReference = useRef<number | null>(null);
  const letters = useRef<
    { char: string; color: string; targetColor: string; colorProgress: number }[]
  >([]);
  const grid = useRef({ columns: 0, rows: 0 });
  const context = useRef<CanvasRenderingContext2D | null>(null);
  const lastGlitchTime = useRef(Date.now());

  const getRandomChar = useCallback(
    () => characters.charAt(Math.floor(Math.random() * characters.length)) || 'A',
    [characters],
  );
  const getRandomColor = useCallback(() => randomItem(glitchColors) ?? '#ffffff', [glitchColors]);

  const drawLetters = useCallback(() => {
    if (!context.current || letters.current.length === 0) return;
    const context_ = context.current;
    const canvas = canvasReference.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    context_.clearRect(0, 0, rect.width, rect.height);
    context_.font = `${String(FONT_SIZE)}px monospace`;
    context_.textBaseline = 'top';
    let index = 0;
    for (const letter of letters.current) {
      const x = (index % grid.current.columns) * CHAR_WIDTH;
      const y = Math.floor(index / grid.current.columns) * CHAR_HEIGHT;
      context_.fillStyle = letter.color;
      context_.fillText(letter.char, x, y);
      index += 1;
    }
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasReference.current;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = parent.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${String(rect.width)}px`;
    canvas.style.height = `${String(rect.height)}px`;
    if (context.current) context.current.setTransform(dpr, 0, 0, dpr, 0, 0);
    grid.current = {
      columns: Math.ceil(rect.width / CHAR_WIDTH),
      rows: Math.ceil(rect.height / CHAR_HEIGHT),
    };
    const total = grid.current.columns * grid.current.rows;
    letters.current = Array.from({ length: total }, () => ({
      char: getRandomChar(),
      color: getRandomColor(),
      targetColor: getRandomColor(),
      colorProgress: 1,
    }));
    drawLetters();
  }, [getRandomChar, getRandomColor, drawLetters]);

  const updateLetters = useCallback(() => {
    if (letters.current.length === 0) return;
    const updateCount = Math.max(1, Math.floor(letters.current.length * 0.05));
    for (let index = 0; index < updateCount; index += 1) {
      const letter = letters.current[Math.floor(Math.random() * letters.current.length)];
      if (!letter) continue;
      letter.char = getRandomChar();
      letter.targetColor = getRandomColor();
      letter.colorProgress = smooth ? 0 : 1;
      if (!smooth) letter.color = letter.targetColor;
    }
  }, [getRandomChar, getRandomColor, smooth]);

  const handleSmoothTransitions = useCallback(() => {
    let isNeedsRedraw = false;
    for (const letter of letters.current) {
      if (letter.colorProgress >= 1) continue;
      letter.colorProgress = Math.min(letter.colorProgress + 0.05, 1);
      const startRgb = hexToRgb(letter.color);
      const endRgb = hexToRgb(letter.targetColor);
      if (startRgb && endRgb) {
        letter.color = interpolateColor(startRgb, endRgb, letter.colorProgress);
        isNeedsRedraw = true;
      }
    }
    if (isNeedsRedraw) drawLetters();
  }, [drawLetters]);

  const animate = useCallback(() => {
    const now = Date.now();
    if (now - lastGlitchTime.current >= glitchSpeed) {
      updateLetters();
      drawLetters();
      lastGlitchTime.current = now;
    }
    if (smooth) handleSmoothTransitions();
    animationReference.current = requestAnimationFrame(animate);
  }, [glitchSpeed, smooth, updateLetters, drawLetters, handleSmoothTransitions]);

  useEffect(() => {
    const canvas = canvasReference.current;
    if (!canvas) return;
    const context_ = canvas.getContext('2d');
    if (!context_) return;
    context.current = context_;
    resizeCanvas();
    animate();
    let resizeTimeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (animationReference.current) cancelAnimationFrame(animationReference.current);
        resizeCanvas();
        animate();
      }, 100);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      if (animationReference.current) cancelAnimationFrame(animationReference.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [animate, resizeCanvas]);

  return (
    <div className="bg-background absolute inset-0 overflow-hidden">
      <canvas ref={canvasReference} className="block h-full w-full" />
      {outerVignette && <div className={`pointer-events-none absolute inset-0 ${VIGOR_OUTER}`} />}
      {centerVignette && <div className={`pointer-events-none absolute inset-0 ${VIGOR_CENTER}`} />}
    </div>
  );
}

export default LetterGlitch;
